import { ChartDataset } from 'chart.js';
import { resolveColorVar } from 'shared-ui';
import { RentalStatsByType, RentalStatsByTypeEntry } from 'types/CarTracking';
import { toTitleCase } from 'utils/helpers';

type DriverTypeVisualConfig = {
    label: string;
    colorToken: string;
    subtitle?: string;
    tooltip?: string;
};

type DriverTypeTile = {
    key: string;
    label: string;
    colorToken: string;
    subtitle?: string;
    count: number;
    percentage: number;
    tooltip?: string;
};

type DriverTypesChartData = {
    labels: string[];
    datasets: Array<ChartDataset<'doughnut'>>;
};

const preferredRentalTypeOrder = [
    'hourly',
    'flat rate',
    'daily',
    'monthly',
    'bulk_rental',
    'enterprise',
    'free_until_hourly',
] as const;

const preferredRentalTypeOrderIndex = preferredRentalTypeOrder.reduce<Record<string, number>>((result, key, index) => {
    result[key] = index;
    return result;
}, {});

const rentalTypeVisualConfig: Record<string, DriverTypeVisualConfig> = {
    hourly: { label: 'Hourly', colorToken: '--ag-orange-9', subtitle: 'Transient' },
    'flat rate': { label: 'Flat Rate', colorToken: '--ag-orange-7', subtitle: 'Transient' },
    daily: { label: 'Daily', colorToken: '--ag-orange-5', subtitle: 'Transient' },
    monthly: { label: 'Individuals', colorToken: '--ag-indigo-9', subtitle: 'Monthly' },
    bulk_rental: { label: 'Tenants', colorToken: '--ag-indigo-7', subtitle: 'Monthly' },
    enterprise: { label: 'Enterprise', colorToken: '--ag-crimson-9' },
    free_until_hourly: { label: 'Free Until Hourly', colorToken: '--ag-violet-10', subtitle: 'Transient' },
};

const fallbackColorTokens = [
    '--ag-indigo-9',
    '--ag-indigo-7',
    '--ag-crimson-9',
    '--ag-violet-10',
    '--ag-teal-10',
    '--ag-amber-9',
];

function sortRentalTypeEntries([keyA]: [string, RentalStatsByTypeEntry], [keyB]: [string, RentalStatsByTypeEntry]) {
    const orderA = preferredRentalTypeOrderIndex[keyA] ?? Number.MAX_SAFE_INTEGER;
    const orderB = preferredRentalTypeOrderIndex[keyB] ?? Number.MAX_SAFE_INTEGER;

    if (orderA !== orderB) {
        return orderA - orderB;
    }

    return keyA.localeCompare(keyB);
}

function getFallbackLabel(rentalType: string): string {
    return toTitleCase(rentalType.replaceAll('_', ' '));
}

export function hasDriverTypeData(report: RentalStatsByType): boolean {
    return Object.values(report.rental_counts).some((entry) => entry.count > 0);
}

export function getDriverTypeTiles(report: RentalStatsByType): DriverTypeTile[] {
    let fallbackColorIndex = 0;
    return Object.entries(report.rental_counts)
        .filter(([, entry]) => entry.count > 0)
        .sort(sortRentalTypeEntries)
        .map(([key, entry]) => {
            const config = rentalTypeVisualConfig[key];
            const fallbackColorToken = fallbackColorTokens[fallbackColorIndex % fallbackColorTokens.length];

            if (!config) {
                fallbackColorIndex += 1;
            }

            return {
                key,
                label: config?.label ?? getFallbackLabel(key),
                colorToken: config?.colorToken ?? fallbackColorToken,
                subtitle: config?.subtitle,
                count: entry.count,
                percentage: entry.percentage,
                tooltip: config?.tooltip,
            };
        });
}

export function getDriverTypeChartData(
    tiles: DriverTypeTile[],
    resolveColorToken = resolveColorVar
): DriverTypesChartData {
    return {
        labels: tiles.map((tile) => tile.label),
        datasets: [
            {
                data: tiles.map((tile) => tile.count),
                backgroundColor: tiles.map((tile) => resolveColorToken(tile.colorToken)),
                borderWidth: 0,
            },
        ],
    };
}

export type { DriverTypesChartData, DriverTypeTile };
